import React, { useState, useEffect } from 'react';
import { createVideoBanner, updateVideoBanner, uploadFile, getCountryEnums } from '../../services/adminApi';
import './VideoBannerForm.css';

const VideoBannerForm = ({ banner, onClose }) => {
  const [formData, setFormData] = useState({
    NAME: '',
    VIDEOBANNER: null,
    LINK: '',
    COUNTRY: '',
    ACTIVE: 'Y',
    SORT: 500
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState([]);
  const [videoPreview, setVideoPreview] = useState(null);

  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    if (path.startsWith('/upload')) {
      return `${process.env.REACT_APP_BACKEND_URL || 'https://www.kapussta.com'}${path}`;
    }
    return path;
  };

  const loadCountries = async () => {
    try {
      const data = await getCountryEnums();
      setCountries(data);
    } catch (error) {
      console.error('Error loading countries:', error);
    }
  };

  useEffect(() => {
    loadCountries();
    if (banner) {
      setFormData({
        NAME: banner.NAME || '',
        VIDEOBANNER: banner.VIDEOBANNER_FILE_ID || banner.VIDEO_PATH || null,
        LINK: banner.LINK || '',
        COUNTRY: banner.COUNTRY || '',
        ACTIVE: banner.ACTIVE || 'Y',
        SORT: banner.SORT || 500
      });
      if (banner.VIDEO_PATH) {
        setVideoPreview(getImageUrl(banner.VIDEO_PATH));
      }
    }
  }, [banner]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadFile(file);
      setFormData({ ...formData, VIDEOBANNER: result.fileId });
      
      // Create preview URL
      if (file.type.startsWith('video/')) {
        setVideoPreview(URL.createObjectURL(file));
      } else {
        setVideoPreview(URL.createObjectURL(file));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Не удалось загрузить файл');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const bannerData = {
        NAME: formData.NAME,
        VIDEOBANNER: formData.VIDEOBANNER,
        LINK: formData.LINK,
        COUNTRY: formData.COUNTRY || null,
        ACTIVE: formData.ACTIVE,
        SORT: parseInt(formData.SORT)
      };

      if (banner) {
        await updateVideoBanner(banner.ID, bannerData);
      } else {
        await createVideoBanner(bannerData);
      }
      
      onClose();
    } catch (error) {
      console.error('Save error:', error);
      alert('Не удалось сохранить видео баннер');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{banner ? 'Редактировать видео баннер' : 'Создать видео баннер'}</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <form onSubmit={handleSubmit} className="video-banner-form">
          <div className="form-group">
            <label>Название *</label>
            <input
              type="text"
              value={formData.NAME}
              onChange={(e) => setFormData({ ...formData, NAME: e.target.value })}
              required
              placeholder="Название баннера"
            />
          </div>

          <div className="form-group">
            <label>Видео/Изображение *</label>
            <input
              type="file"
              accept="video/*,image/*"
              onChange={handleFileChange}
              disabled={uploading}
            />
            {uploading && <span className="upload-status">Загрузка...</span>}
            {formData.VIDEOBANNER && !uploading && (
              <span className="upload-status">✓ Файл загружен</span>
            )}
            {videoPreview && (
              <div className="preview-container">
                {formData.VIDEOBANNER && typeof formData.VIDEOBANNER === 'string' && formData.VIDEOBANNER.includes('.mp4') ? (
                  <video 
                    src={videoPreview} 
                    className="preview-media"
                    controls
                    muted
                  />
                ) : (
                  <img 
                    src={videoPreview} 
                    alt="Preview" 
                    className="preview-media"
                  />
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Ссылка (LINK)</label>
            <input
              type="url"
              value={formData.LINK}
              onChange={(e) => setFormData({ ...formData, LINK: e.target.value })}
              placeholder="https://example.com или https://t.me/channel"
            />
          </div>

          <div className="form-group">
            <label>Страна (COUNTRY)</label>
            <select
              value={formData.COUNTRY}
              onChange={(e) => setFormData({ ...formData, COUNTRY: e.target.value })}
            >
              <option value="">Все страны</option>
              {countries.map((country) => (
                <option key={country.ID} value={country.XML_ID}>
                  {country.VALUE}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Активен</label>
              <select
                value={formData.ACTIVE}
                onChange={(e) => setFormData({ ...formData, ACTIVE: e.target.value })}
              >
                <option value="Y">Да</option>
                <option value="N">Нет</option>
              </select>
            </div>

            <div className="form-group">
              <label>Порядок сортировки</label>
              <input
                type="number"
                min="1"
                value={formData.SORT}
                onChange={(e) => setFormData({ ...formData, SORT: e.target.value })}
                placeholder="1, 2, 3... (500 = по умолчанию)"
              />
              <small style={{ color: '#b0b0b0', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Введите позицию (1, 2, 3...). Если позиция занята, другие баннеры автоматически сдвинутся.
              </small>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-cancel">Отмена</button>
            <button type="submit" disabled={saving} className="btn-save">
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VideoBannerForm;

